import React, { useState } from 'react';
import { Button, Card, makeStyles, tokens, Text } from '@fluentui/react-components';
import { AgentMessageErrorBoundary } from './AgentMessageErrorBoundary';
import { withAgentMessageErrorBoundary, useAgentMessageErrorHandler } from './withAgentMessageErrorBoundary';
import { AgentMessageData, AgentMessageType } from '@/models';

const useStyles = makeStyles({
  container: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    maxWidth: '800px',
    margin: '0 auto'
  },
  demoCard: {
    padding: '16px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px'
  },
  buttonGroup: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap'
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: '12px'
  },
  description: {
    color: tokens.colorNeutralForeground2,
    marginBottom: '16px'
  }
});

// Component that can simulate various error conditions
const ErrorProneAgentMessage: React.FC<{
  errorType: 'none' | 'render' | 'icon' | 'content' | 'validation';
  message?: AgentMessageData;
}> = ({ errorType, message }) => {
  const { reportError, safeSync } = useAgentMessageErrorHandler();

  const renderContent = safeSync(() => {
    if (errorType === 'render') {
      throw new Error('Simulated rendering error in agent message');
    }

    if (errorType === 'icon') {
      // Simulate icon rendering error
      throw new Error('Failed to load agent icon');
    }

    if (errorType === 'content') {
      // Simulate content processing error
      throw new Error('Failed to process message content');
    }

    if (errorType === 'validation') {
      // Simulate validation error
      reportError(new Error('Message validation failed'), {
        component: 'ErrorProneAgentMessage',
        originalData: message
      });
      return null;
    }

    return (
      <div style={{ 
        padding: '12px', 
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: '8px',
        border: `1px solid ${tokens.colorNeutralStroke2}`
      }}>
        <Text weight="semibold">{message?.agent || 'Test Agent'}</Text>
        <div style={{ marginTop: '8px' }}>
          <Text>{message?.content || 'This is a test agent message that renders successfully.'}</Text>
        </div>
      </div>
    );
  }, {
    component: 'ErrorProneAgentMessage',
    function: 'renderContent'
  });

  return <>{renderContent()}</>;
};

// Enhanced component with error boundary
const SafeErrorProneAgentMessage = withAgentMessageErrorBoundary(ErrorProneAgentMessage, {
  componentName: 'Safe Agent Message',
  showRetry: true
});

/**
 * Demo component showcasing error boundary functionality
 */
export const ErrorBoundaryDemo: React.FC = () => {
  const styles = useStyles();
  const [errorType, setErrorType] = useState<'none' | 'render' | 'icon' | 'content' | 'validation'>('none');
  const [resetKey, setResetKey] = useState(0);

  const sampleMessage: AgentMessageData = {
    agent: 'Demo Agent',
    agent_type: AgentMessageType.AI_AGENT,
    content: 'This is a sample agent message for testing error boundaries.',
    timestamp: Date.now(),
    steps: [],
    next_steps: [],
    raw_data: ''
  };

  const handleReset = () => {
    setErrorType('none');
    setResetKey(prev => prev + 1);
  };

  return (
    <div className={styles.container}>
      <Card className={styles.demoCard}>
        <Text className={styles.title}>Error Boundary Demo</Text>
        <Text className={styles.description}>
          This demo shows how error boundaries protect agent message components from crashes.
          Try different error scenarios to see how the system handles them gracefully.
        </Text>

        <div className={styles.buttonGroup}>
          <Button 
            appearance="secondary" 
            onClick={() => setErrorType('none')}
            disabled={errorType === 'none'}
          >
            No Error
          </Button>
          <Button 
            appearance="secondary" 
            onClick={() => setErrorType('render')}
            disabled={errorType === 'render'}
          >
            Render Error
          </Button>
          <Button 
            appearance="secondary" 
            onClick={() => setErrorType('icon')}
            disabled={errorType === 'icon'}
          >
            Icon Error
          </Button>
          <Button 
            appearance="secondary" 
            onClick={() => setErrorType('content')}
            disabled={errorType === 'content'}
          >
            Content Error
          </Button>
          <Button 
            appearance="secondary" 
            onClick={() => setErrorType('validation')}
            disabled={errorType === 'validation'}
          >
            Validation Error
          </Button>
          <Button 
            appearance="primary" 
            onClick={handleReset}
          >
            Reset Demo
          </Button>
        </div>
      </Card>

      <Card className={styles.demoCard}>
        <Text className={styles.title}>Component with Error Boundary</Text>
        <Text className={styles.description}>
          This component is wrapped with an error boundary and will show error UI if something goes wrong:
        </Text>

        <AgentMessageErrorBoundary
          key={`boundary-${resetKey}`}
          componentName="Demo Agent Message"
          showRetry={true}
        >
          <SafeErrorProneAgentMessage 
            errorType={errorType}
            message={sampleMessage}
          />
        </AgentMessageErrorBoundary>
      </Card>

      <Card className={styles.demoCard}>
        <Text className={styles.title}>Component without Error Boundary</Text>
        <Text className={styles.description}>
          This component is NOT protected by an error boundary. If it crashes, it could break the entire page:
        </Text>

        <div style={{ 
          padding: '12px', 
          backgroundColor: tokens.colorPaletteRedBackground1,
          borderRadius: '8px',
          border: `1px solid ${tokens.colorPaletteRedBorder1}`
        }}>
          <Text style={{ color: tokens.colorPaletteRedForeground1 }}>
            ⚠️ Unprotected component - would crash the entire page if an error occurred
          </Text>
        </div>
      </Card>

      <Card className={styles.demoCard}>
        <Text className={styles.title}>Multiple Protected Components</Text>
        <Text className={styles.description}>
          Multiple components with individual error boundaries - one failure doesn't affect others:
        </Text>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <AgentMessageErrorBoundary componentName="Message 1" showRetry={false}>
            <SafeErrorProneAgentMessage 
              errorType="none"
              message={{
                ...sampleMessage,
                agent: 'Agent 1',
                content: 'This message renders successfully.'
              }}
            />
          </AgentMessageErrorBoundary>

          <AgentMessageErrorBoundary componentName="Message 2" showRetry={false}>
            <SafeErrorProneAgentMessage 
              errorType={errorType === 'render' ? 'render' : 'none'}
              message={{
                ...sampleMessage,
                agent: 'Agent 2',
                content: 'This message may have an error depending on the selected error type.'
              }}
            />
          </AgentMessageErrorBoundary>

          <AgentMessageErrorBoundary componentName="Message 3" showRetry={false}>
            <SafeErrorProneAgentMessage 
              errorType="none"
              message={{
                ...sampleMessage,
                agent: 'Agent 3',
                content: 'This message always renders successfully, even if others fail.'
              }}
            />
          </AgentMessageErrorBoundary>
        </div>
      </Card>
    </div>
  );
};

export default ErrorBoundaryDemo;